/**
 * When a validation gap is shown. A new document starts quiet: a required
 * field turns red only once the user has edited it and left it (touched),
 * or once a step transition ran the full check (`revealAll`). A field that
 * is already shown in error re-validates on every change, so it clears the
 * moment it becomes valid. Imported or copied contracts start revealed.
 *
 * Client state only, reset per contract. Pure core + a small React hook.
 */

import { useCallback, useMemo, useState } from 'react';

export type TouchState = {
  /** Edited since the last blur — not yet judged. */
  readonly edited: ReadonlySet<string>;
  /** Edited and blurred: gaps on these keys are shown. */
  readonly touched: ReadonlySet<string>;
  /** A step transition (or an import/copy) revealed every gap. */
  readonly revealAll: boolean;
};

export const initialTouchState = (revealAll = false): TouchState => ({
  edited: new Set(),
  touched: new Set(),
  revealAll,
});

/**
 * A change to `key`. While the field is shown in error it stays touched
 * (validate on change); otherwise it waits for the blur before it is
 * judged again, so typing into a valid field never flashes red.
 */
export function markEdited(s: TouchState, key: string, shownInError: boolean): TouchState {
  if (shownInError) {
    return s.touched.has(key) ? s : { ...s, touched: new Set(s.touched).add(key) };
  }
  const touched = s.touched.has(key) ? withoutKey(s.touched, key) : s.touched;
  if (touched === s.touched && s.edited.has(key)) return s;
  return { ...s, touched, edited: new Set(s.edited).add(key) };
}

/** Focus left `key`'s control: an edited field becomes touched. */
export function markBlurred(s: TouchState, key: string): TouchState {
  if (!s.edited.has(key)) return s;
  return { ...s, edited: withoutKey(s.edited, key), touched: new Set(s.touched).add(key) };
}

export const revealAll = (s: TouchState): TouchState => (s.revealAll ? s : { ...s, revealAll: true });

export const isShown = (s: TouchState, key: string): boolean => s.revealAll || s.touched.has(key);

/** The missing keys whose gap is shown now, in the given order. */
export const shownGaps = (s: TouchState, missingKeys: ReadonlyArray<string>): string[] =>
  missingKeys.filter((k) => isShown(s, k));

/** Required-field progress: how many of the mandatory fields hold a value. */
export function requiredProgress(
  requiredKeys: ReadonlyArray<string>,
  missingKeys: ReadonlyArray<string>,
): { filled: number; total: number } {
  const all = new Set([...requiredKeys, ...missingKeys]);
  const missing = new Set(missingKeys.filter((k) => all.has(k)));
  return { filled: all.size - missing.size, total: all.size };
}

/**
 * The next empty required field after `current` in form order, wrapping
 * to the first; the first one when `current` is not a gap or unset.
 */
export function nextEmpty(
  orderedMissingKeys: ReadonlyArray<string>,
  current: string | null,
): string | null {
  if (orderedMissingKeys.length === 0) return null;
  const at = current === null ? -1 : orderedMissingKeys.indexOf(current);
  return orderedMissingKeys[(at + 1) % orderedMissingKeys.length] ?? null;
}

type SlotDiagnostic = { slot_key: string | null };

/**
 * Splits compiler diagnostics: value-level ones (tied to a slot key)
 * follow the touched/revealed rule; structural ones (no slot key — a
 * missing party, an unknown block) are always listed, as a quiet notice.
 */
export function partitionDiagnostics<D extends SlotDiagnostic>(
  diagnostics: ReadonlyArray<D>,
  s: TouchState,
): { value: D[]; hidden: number; structural: D[] } {
  const value: D[] = [];
  const structural: D[] = [];
  let hidden = 0;
  for (const d of diagnostics) {
    if (!d.slot_key) structural.push(d);
    else if (isShown(s, d.slot_key)) value.push(d);
    else hidden++;
  }
  return { value, hidden, structural };
}

type RuleLike = { slotKeys: ReadonlyArray<string> };

/**
 * Block validation rules (ADR-0101) follow the same rule as gaps: a rule
 * is shown once any field it reads is touched, or after a step transition.
 */
export const shownRuleIssues = <R extends RuleLike>(s: TouchState, issues: ReadonlyArray<R>): R[] =>
  issues.filter((i) => s.revealAll || i.slotKeys.some((k) => s.touched.has(k)));

/** React binding: per-contract touch state, reset when `contractKey` changes. */
export function useFormTouched(contractKey: string | null, startRevealed: boolean) {
  const [entry, setEntry] = useState<{ key: string | null; state: TouchState }>(() => ({
    key: contractKey,
    state: initialTouchState(startRevealed),
  }));
  const current =
    entry.key === contractKey ? entry.state : initialTouchState(startRevealed);
  if (entry.key !== contractKey) setEntry({ key: contractKey, state: current });

  const update = useCallback(
    (fn: (s: TouchState) => TouchState) =>
      setEntry((e) => (e.key === contractKey ? { key: e.key, state: fn(e.state) } : e)),
    [contractKey],
  );

  const actions = useMemo(
    () => ({
      edited: (key: string, shownInError: boolean) => update((s) => markEdited(s, key, shownInError)),
      blurred: (key: string) => update((s) => markBlurred(s, key)),
      revealAll: () => update(revealAll),
    }),
    [update],
  );
  return { state: current, ...actions };
}

function withoutKey(set: ReadonlySet<string>, key: string): Set<string> {
  const next = new Set(set);
  next.delete(key);
  return next;
}
